import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { Artist } from '@/types';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LanguageIcon from '@mui/icons-material/Language';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import LocationOnIcon from '@mui/icons-material/LocationOn';

const ArtistInfo: React.FC<{ artist: Artist }> = ({ artist }) => {
  const [expanded, setExpanded] = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const toggleExpand = () => setExpanded((prev) => !prev);

  const getSocialIcon = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('facebook')) return <FacebookIcon fontSize="small" />;
    if (p.includes('twitter') || p.includes('x')) return <TwitterIcon fontSize="small" />;
    if (p.includes('instagram')) return <InstagramIcon fontSize="small" />;
    if (p.includes('youtube')) return <YouTubeIcon fontSize="small" />;
    return <LanguageIcon fontSize="small" />;
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Social Links (above heading) */}
      {artist.social_links && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Stack direction="row" spacing={1}>
            {Object.entries(artist.social_links).map(([platform, url]) =>
              url ? (
                <IconButton
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white' }}
                >
                  {getSocialIcon(platform)}
                </IconButton>
              ) : null
            )}
          </Stack>
        </Box>
      )}

      {/* Heading */}
      <Typography variant="h5" gutterBottom>
        About {artist.name}
      </Typography>

      {/* Bio */}
      <Box sx={{ position: 'relative' }}>
      {artist.bio ? (
  <>
    <Typography
      variant="body1"
      color="text.secondary"
      whiteSpace="pre-line"
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: expanded ? 'none' : 4,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {artist.bio}
    </Typography>

    {/* Inline See More */}
    {!expanded && (
      <Typography
        variant="body2"
        onClick={toggleExpand}
        sx={{
          color: '#E4FF1A',
          cursor: 'pointer',
          fontWeight: 500,
          mt: 1,
          textAlign: 'right',
        }}
      >
        See more
      </Typography>
    )}

    {expanded && (
      <Typography
        variant="body2"
        onClick={toggleExpand}
        sx={{
          color: '#E4FF1A',
          cursor: 'pointer',
          fontWeight: 500,
          mt: 1,
          textAlign: 'right',
        }}
      >
        Show less
      </Typography>
    )}
  </>
) : (
  <Typography variant="body1" color="text.secondary">
    This artist has no biography yet.
  </Typography>
)}
      </Box>

      {/* Location (City/State & Country) */}
      {(artist.city || artist.country) && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationOnIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
            {typeof artist.city === 'string' ? artist.city : null}
            {artist.city && artist.country && ', '}
            {artist.country}
          </Typography>
        </Box>
      )}

      {/* Genre and Mood chips */}
<Box sx={{ mt: 3, mb: 3 }}>
  {!!artist.genre?.length && (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <MusicNoteIcon sx={{ mr: 1 }} /> Genre
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {artist.genre!.map((g, i) => (
          <Chip key={i} label={g} color="primary" variant="outlined" sx={{ mb: 1 }} />
        ))}
      </Stack>
    </Box>
  )}

  {!!artist.mood?.length && (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <EmojiEmotionsIcon sx={{ mr: 1 }} /> Mood
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {artist.mood!.map((m, i) => (
          <Chip key={i} label={m} color="secondary" variant="outlined" sx={{ mb: 1 }} />
        ))}
      </Stack>
    </Box>
  )}
</Box>

    </Box>
  );
};

export default ArtistInfo;

